import { useCallback } from 'react';
import styled from 'styled-components';
import UIRouterLink from 'ui-addon-react-router-dom/UIRouterLink';
import UIStepIndicator from 'UIComponents/connectedStep/UIStepIndicator';
import UIDropdown from 'UIComponents/dropdown/UIDropdown';
import { H7 } from 'UIComponents/elements/headings';
import UIIconCircle from 'UIComponents/icon/UIIconCircle';
import UIFlex from 'UIComponents/layout/UIFlex';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { theme } from 'academy-common-lib/utils/Styling';
import UsageTracker from '../../../utils/UsageTracker';
import { usePlaylist } from '../../savedItems';
import { useAllProgress } from '../../userProgress';
import { useIncorrectPlaylistRedirect } from '../hooks';
import { getListingUrl } from '../../../utils/RoutingUtils';
import { useHasGate } from '../../accounts';
import UIScrollContainer from 'UIComponents/scroll/UIScrollContainer';
import HR from 'UIComponents/elements/HR';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StepIndicator = styled(UIStepIndicator).withConfig({
  displayName: "PlaylistNavigationDropdown__StepIndicator",
  componentId: "az0hv7-0"
})(["[class*='step-indicator__item']{padding-bottom:4px !important;margin-top:0 !important;}[class*='step-indicator__line']{z-index:1 !important;transform:translate(16px,12px);}& > div{padding:8px 16px;}& > div:last-child{overflow:hidden;}& > div[role='listitem']{&::before{content:'';display:block;position:absolute;width:100%;height:100%;background-color:", ";top:0px;left:0;}& > div{z-index:1;}}"], theme.colors.CALYPSO_LIGHT);
const PlaylistItem = styled(UIRouterLink).withConfig({
  displayName: "PlaylistNavigationDropdown__PlaylistItem",
  componentId: "az0hv7-1"
})(["font-size:14px;font-weight:400;text-transform:none;transform:translateY(2px);display:block;color:", ";&:hover{text-decoration:none;color:", ";}"], theme.colors.OBSIDIAN, theme.colors.EERIE);
const PlaylistNavigationDropdown = ({
  item,
  buttonTitle
}) => {
  const {
    id,
    type,
    playlistId
  } = item;
  const {
    playlist,
    playlistLoading,
    playlistError
  } = usePlaylist(playlistId, !playlistId);
  const {
    allProgress
  } = useAllProgress();
  useIncorrectPlaylistRedirect(item);
  const getStepUrl = useCallback((itemId, itemType, language) => getListingUrl({
    id: itemId,
    type: itemType,
    language,
    appearsIn: 'playlist-dropdown',
    isStarted: true,
    playlistId
  }), [playlistId]);
  const StepIcon = useCallback(({
    index,
    stepIndex,
    defaultIcon
  }) => {
    if (!playlist || !allProgress) return defaultIcon;
    const {
      itemId,
      type: itemType,
      language
    } = playlist.items[index];
    let status = 'NOT_STARTED';
    const trackProgress = allProgress.assetProgress.find(asset => asset.type === itemType && asset.id === itemId);
    if (trackProgress) {
      status = trackProgress.status;
    }
    const isActive = index === stepIndex;
    const isCompleted = status === 'COMPLETED';
    const iconName = isCompleted ? 'success' : 'blank';
    let backgroundColor = theme.colors.OLAF;
    let borderColor = theme.colors.BATTLESHIP;
    let color = theme.colors.OLAF;
    if (isActive) {
      backgroundColor = theme.colors.OLAF;
      color = isCompleted ? theme.colors.LORAX : theme.colors.OLAF;
      borderColor = theme.colors.LORAX;
    } else if (isCompleted) {
      backgroundColor = theme.colors.LORAX;
      color = theme.colors.OLAF;
      borderColor = theme.colors.LORAX;
    }
    return /*#__PURE__*/_jsx(UIRouterLink, {
      style: {
        lineHeight: 'inherit'
      },
      to: getStepUrl(itemId, itemType, language),
      use: "unstyled",
      children: /*#__PURE__*/_jsx(UIIconCircle, {
        backgroundColor: backgroundColor,
        borderColor: borderColor,
        borderWidth: 2,
        color: color,
        name: iconName,
        size: 6
      })
    });
  }, [allProgress, getStepUrl, playlist]);
  const handleOnStepClick = index => {
    if (!playlist) return;
    const {
      itemId
    } = playlist.items[index];
    UsageTracker.track('playlistInteraction', {
      action: 'navigator-dropdown-item-clicked',
      playlistId,
      id,
      type,
      to: itemId
    });
  };
  const hasShortsGate = useHasGate('Academy:ShortFormContent');
  if (playlistLoading || playlistError || !playlist) return null;
  return /*#__PURE__*/_jsx("div", {
    style: {
      marginBottom: -4
    },
    children: /*#__PURE__*/_jsx(UIDropdown, {
      buttonText: buttonTitle || playlist.name,
      buttonUse: "link",
      "data-test-id": "playlist-navigator",
      onOpenComplete: () => {
        UsageTracker.track('playlistInteraction', {
          action: 'navigator-dropdown-open',
          playlistId,
          id,
          type
        });
      },
      placement: "top right",
      use: "unstyled",
      children: /*#__PURE__*/_jsxs(UIFlex, {
        direction: "column",
        children: [/*#__PURE__*/_jsx(H7, {
          className: "m-x-4 m-y-4",
          children: playlist.name
        }), /*#__PURE__*/_jsx(HR, {
          distance: "flush"
        }), /*#__PURE__*/_jsx(UIScrollContainer, {
          maxHeight: "50vh",
          children: /*#__PURE__*/_jsx(StepIndicator, {
            StepIconComponent: StepIcon,
            className: "m-all-0",
            direction: "vertical",
            onStepClick: handleOnStepClick,
            stepIndex: playlist.items.findIndex(({
              itemId
            }) => itemId === id),
            stepNames: playlist.items.filter(currentItem => hasShortsGate || currentItem.type !== 'SHORT_FORM_CONTENT').map(({
              itemId,
              type: itemType,
              name: itemName,
              language
            }) => /*#__PURE__*/_jsx(PlaylistItem, {
              className: itemId === id ? 'selected' : '',
              "data-test-id": "playlist-navigator-item",
              "data-test-item-id": itemId,
              "data-test-item-type": itemType,
              to: getStepUrl(itemId, itemType, language),
              use: "dark",
              children: itemName
            }, itemName))
          })
        }), /*#__PURE__*/_jsx(HR, {
          distance: "flush"
        }), /*#__PURE__*/_jsx(UIRouterLink, {
          className: "m-x-4 m-y-2",
          onClick: () => {
            UsageTracker.track('playlistInteraction', {
              action: 'navigator-dropdown-view-playlist-clicked',
              playlistId,
              id,
              type
            });
          },
          to: `/saved-items/playlists/${playlistId}`,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.playlists.viewPlaylist"
          })
        })]
      })
    })
  });
};
export default PlaylistNavigationDropdown;